import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

// Create theme configuration function that accepts mode (light/dark), density, and custom colors
const createAppTheme = (mode = "dark", density = "standard", primaryColor = null, secondaryColor = null) => {
  // Define common color values - New Palette (can be overridden by parameters)
  const primaryMain = primaryColor || "#265D97"; // Professional Blue
  const primaryLight = "#558BBF";
  const primaryDark = "#003566";

  const secondaryMain = secondaryColor || "#6C757D"; // Medium Gray
  const secondaryLight = "#A9B0B7";
  const secondaryDark = "#495057";

  const warningMain = "#F28C28"; // Muted Orange (ensure good contrast for text)
  const successMain = "#28A745"; // Standard Green
  const errorMain = "#DC3545";   // Standard Red
  const infoMain = "#17A2B8";    // Standard Info Blue

  // Define density spacing values
  const densityFactors = {
    compact: 0.8,
    standard: 1,
    comfortable: 1.2,
  };

  const densityFactor = densityFactors[density];

  // Define common styling values that will be reused
  const borderRadius = 8;

  // Create base theme first (without components that reference theme)
  const baseTheme = createTheme({
    // Color palette with dark mode support
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: mode === "dark" ? "#FFFFFF" : "#FFFFFF",
      },
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: mode === "dark" ? "#FFFFFF" : "#FFFFFF",
      },
      error: {
        main: errorMain,
        light: mode === "dark" ? "#E57373" : "#EF5350", // Lighter red for light mode if needed
        dark: mode === "dark" ? "#D32F2F" : "#C62828",   // Darker red for dark mode if needed
        contrastText: "#FFFFFF",
      },
      warning: {
        main: warningMain,
        light: mode === "dark" ? "#FFB74D" : "#FFA726", // Lighter for light mode if needed
        dark: mode === "dark" ? "#F57C00" : "#E65100",   // Darker for dark mode if needed
        contrastText: "#000000", // Orange often needs dark text for contrast
      },
      success: {
        main: successMain,
        light: mode === "dark" ? "#81C784" : "#66BB6A",
        dark: mode === "dark" ? "#388E3C" : "#2E7D32",
        contrastText: "#FFFFFF",
      },
      info: {
        main: infoMain,
        light: mode === "dark" ? "#4FC3F7" : "#29B6F6",
        dark: mode === "dark" ? "#0288D1" : "#0277BD",
        contrastText: "#FFFFFF",
      },
      grey: {
        ...grey,
        lightest: mode === "dark" ? "#313B47" : "#F9FAFB", // Adjusted for new dark theme
      },
      background: {
        default: mode === "dark" ? "#121821" : "#F4F6F8", // Very Dark Blue/Gray for dark, Light Gray for light
        paper: mode === "dark" ? "#1B232E" : "#FFFFFF", // Slightly lighter for cards in dark, White for light
        subtle: mode === "dark" ? "#161C24" : "#F0F2F5", // Subtle background variants
      },
      text: {
        primary: mode === "dark" ? "#E0E6EB" : "#1F2937", // Light gray for dark, Dark gray for light
        secondary: mode === "dark" ? "#B0B8C4" : "#6B7280", // Softer gray
        disabled: mode === "dark" ? "rgba(224, 230, 235, 0.5)" : "rgba(31, 41, 55, 0.38)",
      },
      divider:
        mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)", // Slightly more visible divider
      action: {
        active: mode === "dark" ? "#E0E6EB" : "rgba(0, 0, 0, 0.54)", // Use text primary for active icons
        hover:
          mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)", // Standard hover
        hoverOpacity: 0.08,
        selected:
          mode === "dark" ? "rgba(38, 93, 151, 0.16)" : "rgba(38, 93, 151, 0.08)", // Using primary color with alpha
        selectedOpacity: mode === "dark" ? 0.16 : 0.08,
        disabled:
          mode === "dark" ? "rgba(224, 230, 235, 0.3)" : "rgba(31, 41, 55, 0.26)",
        disabledBackground:
          mode === "dark" ? "rgba(224, 230, 235, 0.12)" : "rgba(31, 41, 55, 0.12)",
        disabledOpacity: 0.38,
        focus:
          mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
    },

    // Typography settings - adjust for more modern look
    typography: {
      fontFamily: 'Inter, Roboto, "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: 1.2,
        letterSpacing: "-0.01562em",
      },
      h2: {
        fontWeight: 700,
        fontSize: "2rem",
        lineHeight: 1.2,
        letterSpacing: "-0.00833em",
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.2,
        letterSpacing: "0em",
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.2,
        letterSpacing: "0.00735em",
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.2,
        letterSpacing: "0em",
      },
      h6: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.2,
        letterSpacing: "0.0075em",
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: "0.00714em",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.43,
        letterSpacing: "0.01071em",
      },
      button: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: "0.02857em",
        textTransform: "none", // Modern buttons often don't use text-transform
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.66,
        letterSpacing: "0.03333em",
      },
      overline: {
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: 2.66,
        letterSpacing: "0.08333em",
        textTransform: "uppercase",
      },
    },

    // Shape settings
    shape: {
      borderRadius: borderRadius, // Use the defined borderRadius constant
    },

    // Custom spacing system with density support
    spacing: (factor) => `${0.25 * factor * densityFactor}rem`,

    // Animation standards with enhanced motion system
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
        buttonTap: 100,
        tooltipEnter: 150,
        menuEnter: 200,
        dialogEnter: 300,
      },
      easing: {
        easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
        easeIn: "cubic-bezier(0.4, 0, 1, 1)",
        sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
        emphasis: "cubic-bezier(0.2, 0.9, 0.1, 1.2)",
        bounce: "cubic-bezier(0.1, 1.1, 0.2, 1.05)",
      },
    },

    // Motion system for custom animations
    motion: {
      elevation: {
        transition:
          "box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        hover: "transform: translateY(-2px)",
        active: "transform: translateY(1px)",
      },
      fadeIn: {
        transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
        initial: "opacity: 0",
        animate: "opacity: 1",
      },
      slideUp: {
        transition: "transform 225ms cubic-bezier(0.0, 0, 0.2, 1)",
        initial: "transform: translateY(10px)",
        animate: "transform: translateY(0)",
      },
      scale: {
        transition: "transform 200ms cubic-bezier(0.2, 0.9, 0.1, 1.2)",
        initial: "transform: scale(0.95)",
        animate: "transform: scale(1)",
      },
    },

    // Density configuration
    density: density,
  });

  // Now create the full theme with components that can reference the base theme
  const theme = createTheme({
    ...baseTheme,
    // Custom component overrides
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: "background-color 0.3s ease, color 0.3s ease",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "dark" ? baseTheme.palette.background.paper : "#e0e0e0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "dark" ? secondaryMain : grey[500],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: mode === "dark" ? secondaryDark : grey[600],
            },
            "& *:focus-visible": {
              outlineWidth: "2px",
              outlineStyle: "solid",
              outlineColor: mode === "dark" ? primaryLight : primaryMain,
              outlineOffset: "2px",
              borderRadius: "4px",
            },
          },
          "::selection": {
            backgroundColor:
              mode === "dark"
                ? primaryMain + "50"
                : primaryMain + "40",
            color: mode === "dark" ? "#FFFFFF" : "#FFFFFF",
          },
          "@keyframes pulseOutline": {
            "0%": { outlineColor: mode === "dark" ? primaryLight + "AA" : primaryMain + "AA" },
            "50%": { outlineColor: mode === "dark" ? primaryLight : primaryMain },
            "100%": { outlineColor: mode === "dark" ? primaryLight + "AA" : primaryMain + "AA" },
          },
          ".focus-visible-pulse:focus-visible": {
            animation: "pulseOutline 1.5s infinite ease-in-out",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8, // Consistent with new shape.borderRadius
            boxShadow: "none",
            fontWeight: 500, // Keep medium weight for buttons
            textTransform: "none",
            padding: `${6 * densityFactor}px ${16 * densityFactor}px`,
            transition: "all 0.2s ease-out", // Slightly faster transition
            position: "relative", // For potential pseudo-elements or ripple
            "&:focus-visible": {
              outline: `2px solid ${mode === "dark" ? primaryLight : primaryMain}`,
              outlineOffset: "2px",
            },
            "&:active": {
              transform: "scale(0.97)", // Slightly more pronounced active scale
            },
          },
          contained: {
            boxShadow:
              mode === "dark"
                ? "0 2px 4px 0 rgba(0, 0, 0, 0.2)" // Softer shadow for dark mode
                : "0 2px 4px 0 rgba(0, 0, 0, 0.1)", // Softer shadow for light mode
            "&:hover": {
              boxShadow: mode === "dark" ? "0px 4px 8px rgba(0, 0, 0, 0.25)" : "0px 4px 8px rgba(0, 0, 0, 0.15)",
              transform: "translateY(-1px)", // Keep subtle lift
            },
          },
          outlined: {
            borderWidth: "1px", // Keep standard border width
            "&:hover": {
              borderWidth: "1px",
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)" // Keep subtle hover for outlined
                  : "rgba(38, 93, 151, 0.04)", // Use primary with alpha for light
            },
          },
          text: {
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)" // Keep subtle hover for text
                  : "rgba(38, 93, 151, 0.04)", // Use primary with alpha for light
            },
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: `${12 * densityFactor}px ${16 * densityFactor}px`,
            borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
          },
          head: {
            fontWeight: 600,
            fontSize: "0.875rem",
            color: mode === "dark" ? grey[300] : grey[700],
            backgroundColor: mode === "dark" ? baseTheme.palette.background.paper : grey[100],
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: mode === "dark" ? baseTheme.palette.background.paper : "#FFFFFF",
            boxShadow:
              mode === "dark"
                ? "0 1px 3px rgba(0, 0, 0, 0.2)" // Softer, more modern shadow for dark
                : "0 1px 3px rgba(0, 0, 0, 0.1)", // Softer, more modern shadow for light
            border: `1px solid ${baseTheme.palette.divider}`,
            borderRadius: borderRadius, // Use the defined borderRadius constant
            overflow: "hidden",
            transition: "box-shadow 0.25s ease-out, transform 0.25s ease-out",
            "&:hover": {
              boxShadow: mode === 'dark'
                ? `0 4px 12px rgba(0, 0, 0, 0.25)`
                : `0 4px 12px rgba(0, 0, 0, 0.12)`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: mode === "dark" ? baseTheme.palette.background.paper : "#FFFFFF",
            borderRadius: borderRadius, // Use the defined borderRadius constant
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? baseTheme.palette.background.paper : primaryMain,
            boxShadow:
              mode === "dark"
                ? "0 1px 2px rgba(0, 0, 0, 0.25)"
                : "0 1px 2px rgba(0, 0, 0, 0.12)",
            color: mode === "dark" ? baseTheme.palette.text.primary : "#FFFFFF",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === "dark" ? grey[800] : grey[700], // Darker tooltip for better contrast
            color: "#FFFFFF",
            fontSize: "0.8rem",
            padding: `${6 * densityFactor}px ${10 * densityFactor}px`,
            borderRadius: borderRadius / 1.5, // Slightly less rounded than cards
          },
          arrow: {
            color: mode === "dark" ? grey[800] : grey[700],
          },
        },
        defaultProps: {
          enterDelay: 500, // Standard delay
          leaveDelay: 100,
          arrow: true,
        },
      },
      MuiLink: {
        variants: [
          {
            props: { variant: "skip-to-content" },
            style: {
              position: "absolute",
              top: "-40px",
              left: "0",
              backgroundColor: mode === "dark" ? primaryDark : primaryMain,
              color: "#FFFFFF",
              padding: "8px 16px",
              zIndex: 2000,
              textDecoration: "none",
              borderRadius: "0 0 4px 0",
              fontWeight: 500,
              transition: "top 0.2s ease",
              "&:focus": {
                top: "0",
              },
            },
          },
        ],
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            boxShadow:
              mode === "dark"
                ? "0 1px 3px rgba(0, 0, 0, 0.2)" // Softer avatar shadow
                : "0 1px 3px rgba(0, 0, 0, 0.1)",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius / 1.5, // Consistent rounding
            fontWeight: 500,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.07)"
                  : "rgba(0, 0, 0, 0.06)",
            },
            "&.Mui-selected": {
              backgroundColor: mode === 'dark' ? primaryDark + "A0" : primaryLight + "A0",
              color: mode === 'dark' ? '#FFFFFF' : primaryDark,
              '&:hover': {
                backgroundColor: mode === 'dark' ? primaryDark + "C0" : primaryLight + "C0",
              }
            }
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius, // Consistent rounding for inputs
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: mode === 'dark' ? primaryLight : primaryMain,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: mode === 'dark' ? primaryLight : primaryMain,
              borderWidth: '2px',
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius, // Consistent rounding
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.06)',
            "&:hover": {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.09)',
            },
            "&.Mui-focused": {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.13)' : 'rgba(0, 0, 0, 0.09)',
            },
            "&::before, &::after": { // Remove underline for filled inputs
              borderBottom: "none",
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? primaryLight : primaryMain,
          },
        }
      }
    },
  });

  // Apply responsive font sizes and return the final theme
  return responsiveFontSizes(theme);
};

const defaultMode = 'dark';
const theme = createAppTheme(defaultMode, "standard");

export { createAppTheme };
export default theme;
